import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  DemandComparison,
  DemandMatrixFilter,
  DemandMatrixRow,
  GetDemandFilterOptionsResponse,
  GetDemandMatrixArgs,
  GetDemandMatrixResponse,
  GetDemandSummaryResponse,
  GetMonthToDateDailyInput,
  GetMonthToDateDailyResponse,
} from '@erp/rpc';

export type {
  GetMonthToDateDailyResponse,
  DemandComparison,
  DemandMatrixFilter,
  DemandMatrixRow,
  GetDemandFilterOptionsResponse,
  GetDemandMatrixArgs,
  GetDemandMatrixResponse,
  GetDemandSummaryResponse,
};

// The three demand-forecast reads are deliberately separate queries rather than
// one page payload: the grid is milliseconds and the summary is a scope-sized
// aggregate, so pairing them would hold the fast half hostage to the slow one.

/** Filter menus and the run's month axis. Cheap enough to fetch eagerly. */
export function useDemandFilterOptions(runId?: string) {
  return useQuery<GetDemandFilterOptionsResponse>({
    ...orpc.procurement.forecastRuns.demandFilterOptions.queryOptions({
      input: { runId },
    }),
    staleTime: 5 * 60_000,
  });
}

/**
 * The grid, as an infinite list.
 *
 * The API is still paged — `page`/`pageSize` with an exact `hasMore` — and the
 * scroll container just asks for the next page when the sentinel comes into
 * view. Paging stays server-side because the fast path's whole trick is a LIMIT
 * that streams off an index; "load everything" would give that up.
 *
 * The query key covers every filter, so changing one starts a fresh list rather
 * than appending to a stale one.
 *
 * Previous rows are held when adding a comparison or history. Hide empty also
 * keeps the old rows visible while the server calculates the new page; the grid
 * labels them as updating and blocks pagination until that page arrives.
 * Location and SKU filters still replace the row set with a skeleton.
 */
type MatrixInput = Omit<GetDemandMatrixArgs, 'page'>;

/** Fields that change what is shown about a row rather than which rows there are. */
const OVERLAY_FIELDS = ['comparisons', 'includeHistory', 'hideEmpty'] as const;

/**
 * Whether two requests select the same rows, ignoring the overlay fields.
 *
 * Compared structurally on the query key rather than by identity: the key is
 * what TanStack hands back, and the input object is rebuilt every render.
 */
function sameRowSet(
  previousKey: readonly unknown[] | undefined,
  next: MatrixInput,
): boolean {
  const previous = previousKey?.[2];
  if (previous === undefined || previous === null) return false;
  const strip = (value: MatrixInput): string => {
    const rest: Record<string, unknown> = { ...value };
    for (const field of OVERLAY_FIELDS) delete rest[field];
    // Key order is stable because both sides come from the same literal.
    return JSON.stringify(rest);
  };
  return strip(previous as MatrixInput) === strip(next);
}

export function useDemandMatrixPages(input: MatrixInput) {
  return useInfiniteQuery({
    queryKey: ['procurement', 'demandMatrix', input],
    queryFn: ({ pageParam }) =>
      rpcClient.procurement.forecastRuns.demandMatrix({
        ...input,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    placeholderData: (previous, previousQuery) =>
      sameRowSet(previousQuery?.queryKey, input) ? previous : undefined,
    staleTime: 30_000,
  });
}

/** A bounded page for Previous/Next navigation, including direct detail links. */
export function useDemandMatrixPage(
  input: GetDemandMatrixArgs,
  enabled: boolean,
  /**
   * Keep showing the last page while a changed input loads. For a view whose
   * question stays the same while a setting changes (the detail page's run
   * comparison), so the table does not collapse and move the page under the
   * reader. Off for navigation, where a stale page is the wrong item.
   */
  keepPrevious = false,
) {
  return useQuery({
    ...orpc.procurement.forecastRuns.demandMatrix.queryOptions({ input }),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
}

/**
 * Headline numbers and the monthly chart. `enabled` is how the collapsed
 * summary panel avoids paying for an aggregate nobody is looking at.
 */
export function useDemandSummary(input: DemandMatrixFilter, enabled: boolean) {
  return useQuery<GetDemandSummaryResponse>({
    ...orpc.procurement.forecastRuns.demandSummary.queryOptions({ input }),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * One series' open month, day by day. Read only while its chart is open, so a
 * page of rows never pays for it.
 */
export function useMonthToDateDaily(
  input: GetMonthToDateDailyInput | null,
  enabled: boolean,
) {
  return useQuery({
    ...orpc.procurement.forecastRuns.monthToDateDaily.queryOptions({
      // Never sent while `input` is null: `enabled` is false then.
      input: input ?? { runId: '', warehouseId: '', sku: '' },
    }),
    enabled: enabled && input !== null,
    staleTime: 5 * 60_000,
  });
}
